/**
 * The client half of self-serve export (CCPA §1798.110).
 *
 * Deliberately tiny, and deliberately carrying NO parameters. The server
 * derives the subject from the caller's token — there is no account id, uid or
 * path in this request, so there is nothing here that could widen what gets
 * read and nothing for a future edit to get wrong. See
 * functions/src/dataRights/exportMyData.ts.
 */
const FUNCTIONS_BASE = 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';

export const EXPORT_MY_DATA_URL = `${FUNCTIONS_BASE}/exportMyData`;
export const REQUEST_DELETION_URL = `${FUNCTIONS_BASE}/requestMyDeletion`;
export const CANCEL_DELETION_URL = `${FUNCTIONS_BASE}/cancelMyDeletion`;

export interface ExportCounts {
  locations: number;
  documents: number;
  objects: number;
}

export type ExportResult =
  | {
    ok: true;
    requestRef: string;
    url: string;
    expiresInMs: number;
    scope: string;
    includesWorkspace: boolean;
    counts: ExportCounts;
    truncated: string[];
  }
  | { ok: false; reason: string; requestRef?: string; message?: string };

/**
 * Ask for a copy of everything held about you.
 *
 * `getToken` is injected rather than imported so this module stays free of
 * firebase/auth — that import touches global fetch AT LOAD TIME and takes
 * whole test suites down before a single test runs (the attachmentStorage
 * lesson, CLAUDE.md).
 */
export async function requestMyDataExport(
  getToken: () => Promise<string | null>,
  fetchImpl: typeof fetch = fetch,
): Promise<ExportResult> {
  const token = await getToken();
  if (!token) {
    return { ok: false, reason: 'unauthenticated', message: 'please sign in again and retry.' };
  }
  let res: Response;
  try {
    res = await fetchImpl(EXPORT_MY_DATA_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      // An empty body, on purpose: nothing the caller sends can change what is
      // read. Sending `{}` rather than nothing keeps the content type honest.
      body: '{}',
    });
  } catch {
    return { ok: false, reason: 'network', message: 'could not reach the server. try again?' };
  }
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { ok: false, reason: 'malformed', message: 'the server sent something unreadable.' };
  }
  const parsed = body as ExportResult;
  // A non-2xx that still parsed carries the server's own honest message — the
  // 'incomplete' case especially, which is a deliberate refusal rather than a
  // crash and reads very differently to a person.
  if (!res.ok && parsed && typeof parsed === 'object') {
    return { ok: false, ...(parsed as object) } as ExportResult;
  }
  return parsed;
}

// ── deletion (CCPA §1798.105) ───────────────────────────────────────────────

export interface DeletionSummary {
  deletes: number;
  anonymises: number;
  keeps: Array<{ what: string; forHowLong: string; why: string }>;
}

export type DeletionRequestResult =
  | {
    ok: true;
    requestRef: string;
    purgeAfter: string;
    graceDays: number;
    includesWorkspace: boolean;
    summary: DeletionSummary;
    alreadyRequested?: false;
  }
  | { ok: true; alreadyRequested: true; requestRef: string; purgeAfter: string | null }
  | { ok: false; reason: string; message?: string };

export type DeletionCancelResult =
  | { ok: true; cancelled?: true; nothingToCancel?: true; requestRef?: string }
  | { ok: false; reason: string; message?: string };

/**
 * Shared POST for both deletion calls.
 *
 * Same shape as the export: an empty body, because the server derives the
 * subject from the token. There is nothing here naming an account, a uid or a
 * request reference — and a request REFERENCE would be the sharp one, since a
 * caller who could supply it could cancel, or schedule, somebody else's.
 */
async function postEmpty(
  url: string,
  getToken: () => Promise<string | null>,
  fetchImpl: typeof fetch,
): Promise<{ ok: false; reason: string; message?: string } | Record<string, unknown>> {
  const token = await getToken();
  if (!token) {
    return { ok: false, reason: 'unauthenticated', message: 'please sign in again and retry.' };
  }
  let res: Response;
  try {
    res = await fetchImpl(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: '{}',
    });
  } catch {
    return { ok: false, reason: 'network', message: 'could not reach the server. try again?' };
  }
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return { ok: false, reason: 'malformed', message: 'the server sent something unreadable.' };
  }
}

/** Ask to be deleted. Records the request and starts the clock; deletes nothing yet. */
export async function requestMyDeletion(
  getToken: () => Promise<string | null>,
  fetchImpl: typeof fetch = fetch,
): Promise<DeletionRequestResult> {
  return (await postEmpty(REQUEST_DELETION_URL, getToken, fetchImpl)) as DeletionRequestResult;
}

/** Change your mind, any time inside the window. */
export async function cancelMyDeletion(
  getToken: () => Promise<string | null>,
  fetchImpl: typeof fetch = fetch,
): Promise<DeletionCancelResult> {
  return (await postEmpty(CANCEL_DELETION_URL, getToken, fetchImpl)) as DeletionCancelResult;
}
